import React, { createContext, FC, ReactNode, useMemo } from 'react';

import { Address, getContract } from 'viem';
import { PublicClient, WalletClient } from 'wagmi';

import { useSession } from '~components/hooks/useSession';
import {
  BalancerVault__factory,
  BalLiquidityGaugeV5__factory,
  BorrowerOperations__factory,
  CollSurplusPool__factory,
  CommunityIssuance__factory,
  CurveFactoryPool__factory,
  ERC20__factory,
  FraxStaking__factory,
  FeeTracker__factory,
  HintHelpers__factory,
  PriceFeedV2__factory,
  SortedTroves__factory,
  StabilityPoolManager__factory,
  StabilityPool__factory,
  TroveManager__factory,
  VestaFarming__factory,
  VestaVaultStaking__factory,
  VestaNFT__factory,
  VestaParameters__factory,
  VestingVesta__factory,
  VSTOracle__factory,
  GLPManager__factory,
  ActivePool__factory,
  AggregatorV3Interface__factory,
  VestaEIR__factory,
  VestaInterestManager__factory,
  VestaDexTrader__factory,
  SavingModuleStabilityPool__factory,
  VestaSavingModule__factory,
  GMXStakingBonus__factory,
} from '~config/contractTypes';
import { ActiveCollateralAssets, getContractAddress } from '~config/protocol';
import { ContractNames, EContractName } from '~config/protocol/contractConfig';
import { NetworkIds } from '~config/rpcConfig';
import {
  FraxFarm,
  BalFarm,
  VestaFarm,
  EVestaAsset,
  ALL_ASSET_VALUES,
  ELPType,
  AllVestaAssets,
  RewardedVaults,
  ChainlinkAssets,
  CHAINLINK_VALUES,
} from '~config/types';

import { ContractsContext, IndexedContract, TContractFactory } from './types';

export const stabilityPoolMappings = (network: NetworkIds) => ({
  [EVestaAsset.DPX]: getContractAddress(EContractName.DPXStabilityPool, network),
  [EVestaAsset.ETH]: getContractAddress(EContractName.ETHStabilityPool, network),
  [EVestaAsset.GMX]: getContractAddress(EContractName.GMXStabilityPool, network),
  [EVestaAsset.gOHM]: getContractAddress(EContractName.gOHMStabilityPool, network),
  [EVestaAsset.renBTC]: getContractAddress(EContractName.renBTCStabilityPool, network),
  [EVestaAsset.sGLP]: getContractAddress(EContractName.GLPStabilityPool, network),
  [EVestaAsset.ARB]: getContractAddress(EContractName.ARBStabilityPool, network),
  [EVestaAsset.wstETH]: getContractAddress(EContractName.wstETHStabilityPool, network),
});

export const chainlinkMappings = (network: NetworkIds) => ({
  BAL: getContractAddress(EContractName.ChainlinkBAL, network),
  DAI: getContractAddress(EContractName.ChainlinkDAI, network),
  FRAX: getContractAddress(EContractName.ChainlinkFRAX, network),
  FXS: getContractAddress(EContractName.ChainlinkFXS, network),
  USDC: getContractAddress(EContractName.ChainlinkUSDC, network),
  USDT: getContractAddress(EContractName.ChainlinkUSDT, network),
});

export const vestaEIRMappings = (network: NetworkIds) => ({
  [EVestaAsset.DPX]: getContractAddress(EContractName.DPXInterestRate, network),
  [EVestaAsset.ETH]: getContractAddress(EContractName.ETHInterestRate, network),
  [EVestaAsset.GMX]: getContractAddress(EContractName.GMXInterestRate, network),
  [EVestaAsset.gOHM]: getContractAddress(EContractName.gOHMInterestRate, network),
  [EVestaAsset.sGLP]: getContractAddress(EContractName.GLPInterestRate, network),
  [EVestaAsset.renBTC]: getContractAddress(EContractName.renBTCStabilityPool, network),
  [EVestaAsset.ARB]: getContractAddress(EContractName.ARBInterestRate, network),
  [EVestaAsset.wstETH]: getContractAddress(EContractName.wstETHInterestRate, network),
});

interface BaseSessionInfo {
  networkId: NetworkIds;
  publicClient: PublicClient;
  walletClient?: WalletClient;
}

export const ContractContext = createContext<ContractsContext | undefined>(undefined);

function makeContract<TAbi extends readonly object[]>(
  { networkId, publicClient, walletClient }: BaseSessionInfo,
  options: {
    contractName?: ContractNames;
    contractAddress?: string;
    abi: TAbi;
  },
) {
  if (!options.contractAddress && !options.contractName)
    throw 'You must specify a contractAddress or contractName';

  const contractAddress = (
    options.contractName
      ? getContractAddress(options.contractName, networkId)
      : options.contractAddress ?? '0x'
  ) as Address;

  return getContract({
    address: contractAddress,
    abi: options.abi,
    publicClient,
    walletClient,
  });
}

function useNormalContract<T extends TContractFactory>(
  base: BaseSessionInfo,
  contractName: EContractName,
  abi: T['abi'],
) {
  return useMemo(
    () =>
      makeContract<T['abi']>(base, {
        contractName,
        abi,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base.networkId, base.walletClient?.account],
  );
}

function useIndexedContract<TIndex extends string, T extends TContractFactory>(
  base: BaseSessionInfo,
  indexValues: TIndex[],
  abi: T['abi'],
  externalAddresses?: { [index in TIndex]: string },
) {
  return useMemo(
    () =>
      indexValues
        .map(indexValue => ({
          asset: indexValue,
          ct: makeContract<T['abi']>(base, {
            contractAddress: !externalAddresses
              ? getContractAddress(indexValue as any, base.networkId)
              : externalAddresses[indexValue],
            abi,
          }),
        }))
        .reduce((acc, ctInfo) => ({ ...acc, [ctInfo.asset]: ctInfo.ct }), {} as IndexedContract<TIndex, T>),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base.networkId, base.walletClient?.account],
  );
}

export const ContractsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const {
    network: { networkId },
    publicClient,
    walletClient,
  } = useSession();

  const base: BaseSessionInfo = {
    networkId,
    publicClient,
    walletClient,
  };

  const PriceFeed = useNormalContract<typeof PriceFeedV2__factory>(
    base,
    EContractName.PriceFeed,
    PriceFeedV2__factory.abi,
  );

  const CommunityIssuance = useNormalContract<typeof CommunityIssuance__factory>(
    base,
    EContractName.CommunityIssuance,
    CommunityIssuance__factory.abi,
  );

  const StabilityPoolManager = useNormalContract<typeof StabilityPoolManager__factory>(
    base,
    EContractName.StabilityPoolManager,
    StabilityPoolManager__factory.abi,
  );

  const VaultManager = useNormalContract<typeof TroveManager__factory>(
    base,
    EContractName.VaultManager,
    TroveManager__factory.abi,
  );

  const VestaParameters = useNormalContract<typeof VestaParameters__factory>(
    base,
    EContractName.VestaParameters,
    VestaParameters__factory.abi,
  );

  const CollateralSurplus = useNormalContract<typeof CollSurplusPool__factory>(
    base,
    EContractName.CollateralSurplusPool,
    CollSurplusPool__factory.abi,
  );

  const BorrowerOperations = useNormalContract<typeof BorrowerOperations__factory>(
    base,
    EContractName.BorrowerOperations,
    BorrowerOperations__factory.abi,
  );

  const VestaVesting = useNormalContract<typeof VestingVesta__factory>(
    base,
    EContractName.VestingVesta,
    VestingVesta__factory.abi,
  );

  const HintHelpers = useNormalContract<typeof HintHelpers__factory>(
    base,
    EContractName.HintHelpers,
    HintHelpers__factory.abi,
  );

  const SortedVaults = useNormalContract<typeof SortedTroves__factory>(
    base,
    EContractName.SortedTroves,
    SortedTroves__factory.abi,
  );

  const OldBalFarm = useNormalContract<typeof BalancerVault__factory>(
    base,
    EContractName.BalancerVault,
    BalancerVault__factory.abi,
  );

  const VSTOracle2 = useNormalContract<typeof VSTOracle__factory>(
    base,
    EContractName.VSTOracle,
    VSTOracle__factory.abi,
  );

  const GLPManager = useNormalContract<typeof GLPManager__factory>(
    base,
    EContractName.GLPManager,
    GLPManager__factory.abi,
  );

  const NFT = useNormalContract<typeof VestaNFT__factory>(
    base,
    EContractName.VestaNFT,
    VestaNFT__factory.abi,
  );

  const ActivePool = useNormalContract<typeof ActivePool__factory>(
    base,
    EContractName.ActivePool,
    ActivePool__factory.abi,
  );

  const VestaTrader = useNormalContract<typeof VestaDexTrader__factory>(
    base,
    EContractName.VestaDexTrader,
    VestaDexTrader__factory.abi,
  );

  const VestaInterestManager = useNormalContract<typeof VestaInterestManager__factory>(
    base,
    EContractName.VestaInterestManager,
    VestaInterestManager__factory.abi,
  );

  const VestaSavingModule = useNormalContract<typeof VestaSavingModule__factory>(
    base,
    EContractName.VestaSavingModule,
    VestaSavingModule__factory.abi,
  );

  const SavingModuleStabilityPool = useNormalContract<typeof SavingModuleStabilityPool__factory>(
    base,
    EContractName.SavingModuleStabilityPool,
    SavingModuleStabilityPool__factory.abi,
  );

  const GMXBonusStakingBonus = useNormalContract<typeof GMXStakingBonus__factory>(
    base,
    EContractName.VestaGMXBonusStaking,
    GMXStakingBonus__factory.abi,
  );

  //contracts indexed by some object type (eg: EVestaAsset)
  const CurveFactoryPool = useIndexedContract<ELPType, typeof CurveFactoryPool__factory>(
    base,
    Object.values(ELPType),
    CurveFactoryPool__factory.abi,
  );

  const StabilityPool = useIndexedContract<EVestaAsset, typeof StabilityPool__factory>(
    base,
    ActiveCollateralAssets,
    StabilityPool__factory.abi,
    stabilityPoolMappings(networkId),
  );

  const Asset = useIndexedContract<AllVestaAssets, typeof ERC20__factory>(
    base,
    Object.values(ALL_ASSET_VALUES),
    ERC20__factory.abi,
  );

  const FeeTracker = useIndexedContract<RewardedVaults, typeof FeeTracker__factory>(
    base,
    [EVestaAsset.GMX, EVestaAsset.sGLP],
    FeeTracker__factory.abi,
    {
      [EVestaAsset.GMX]: getContractAddress(EContractName.GMXFeeTracker, networkId),
      [EVestaAsset.sGLP]: getContractAddress(EContractName.GLPFeeTracker, networkId),
    },
  );

  const VaultRewards = useIndexedContract<RewardedVaults, typeof VestaVaultStaking__factory>(
    base,
    [EVestaAsset.GMX, EVestaAsset.sGLP],
    VestaVaultStaking__factory.abi,
    {
      [EVestaAsset.GMX]: getContractAddress(EContractName.VestaGMXStaking, networkId),
      [EVestaAsset.sGLP]: getContractAddress(EContractName.VestaGLPStaking, networkId),
    },
  );

  const VestaFarms = useIndexedContract<VestaFarm, typeof VestaFarming__factory>(
    base,
    Object.values(VestaFarm),
    VestaFarming__factory.abi,
  );

  const FraxFarms = useIndexedContract<FraxFarm, typeof FraxStaking__factory>(
    base,
    Object.values(FraxFarm),
    FraxStaking__factory.abi,
  );

  const BalGauge = useIndexedContract<BalFarm, typeof BalLiquidityGaugeV5__factory>(
    base,
    Object.values(BalFarm),
    BalLiquidityGaugeV5__factory.abi,
  );

  const ChainlinkOracles = useIndexedContract<ChainlinkAssets, typeof AggregatorV3Interface__factory>(
    base,
    Object.values(CHAINLINK_VALUES),
    AggregatorV3Interface__factory.abi,
    chainlinkMappings(networkId),
  );

  const VestaEIR = useIndexedContract<EVestaAsset, typeof VestaEIR__factory>(
    base,
    ActiveCollateralAssets,
    VestaEIR__factory.abi,
    vestaEIRMappings(networkId),
  );

  return (
    <ContractContext.Provider
      value={{
        normal: {
          [EContractName.ActivePool]: ActivePool,
          [EContractName.PriceFeed]: PriceFeed,
          [EContractName.CommunityIssuance]: CommunityIssuance,
          [EContractName.StabilityPoolManager]: StabilityPoolManager,
          [EContractName.VaultManager]: VaultManager,
          [EContractName.VestaParameters]: VestaParameters,
          [EContractName.CollateralSurplusPool]: CollateralSurplus,
          [EContractName.BorrowerOperations]: BorrowerOperations,
          [EContractName.VestingVesta]: VestaVesting,
          [EContractName.HintHelpers]: HintHelpers,
          [EContractName.SortedTroves]: SortedVaults,
          [EContractName.BalancerVault]: OldBalFarm,
          [EContractName.VestaNFT]: NFT,
          [EContractName.VSTOracle]: VSTOracle2,
          [EContractName.GLPManager]: GLPManager,
          [EContractName.VestaDexTrader]: VestaTrader,
          [EContractName.VestaInterestManager]: VestaInterestManager,
          [EContractName.SavingModuleStabilityPool]: SavingModuleStabilityPool,
          [EContractName.VestaSavingModule]: VestaSavingModule,
          [EContractName.VestaGMXBonusStaking]: GMXBonusStakingBonus,
        },
        indexed: {
          CurveFactoryPool,
          StabilityPool,
          Asset,
          BalGauge,
          VestaFarm: VestaFarms,
          FraxFarm: FraxFarms,
          FeeTracker,
          VaultRewards,
          ChainlinkOracles,
          VestaEIR,
        },
      }}
    >
      {children}
    </ContractContext.Provider>
  );
};
